'use client';
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tabs = Tabs;
exports.TabList = TabList;
exports.Tab = Tab;
exports.TabPanel = TabPanel;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const utils_1 = require("../utils");
const TabsContext = (0, react_1.createContext)(null);
function useTabs() {
    const ctx = (0, react_1.useContext)(TabsContext);
    if (!ctx) {
        throw new Error('Tabs.* components must be used inside <Tabs>');
    }
    return ctx;
}
// Underline tabs (default) — page-section nav. Pill tabs — filters / segmented choices.
// Controlled only: parent owns `value`. The component is presentational.
function Tabs({ value, onChange, variant = 'underline', className, children }) {
    const idBase = (0, react_1.useId)();
    return ((0, jsx_runtime_1.jsx)(TabsContext.Provider, { value: { value, onChange: onChange, variant, idBase }, children: (0, jsx_runtime_1.jsx)("div", { className: className, children: children }) }));
}
function TabList({ className, children, 'aria-label': ariaLabel }) {
    const { variant } = useTabs();
    if (variant === 'pill') {
        return ((0, jsx_runtime_1.jsx)("div", { "aria-label": ariaLabel, className: (0, utils_1.cn)('inline-flex gap-1 rounded-full border border-neutral-100 bg-neutral-50 p-1', className), role: 'tablist', children: children }));
    }
    return ((0, jsx_runtime_1.jsx)("div", { "aria-label": ariaLabel, className: (0, utils_1.cn)('flex gap-7 border-b border-neutral-100', className), role: 'tablist', children: children }));
}
function Tab({ value, disabled, className, children }) {
    const { value: selected, onChange, variant, idBase } = useTabs();
    const isActive = selected === value;
    const tabId = `${idBase}-tab-${value}`;
    const panelId = `${idBase}-panel-${value}`;
    if (variant === 'pill') {
        return ((0, jsx_runtime_1.jsx)("button", { "aria-controls": panelId, "aria-selected": isActive, className: (0, utils_1.cn)('cursor-pointer rounded-full border-0 bg-transparent px-4 py-1.5 text-[13px] font-semibold transition-colors', 'focus-visible:outline-none focus-visible:shadow-[var(--aiq-ring-focus)]', 'disabled:cursor-not-allowed disabled:opacity-50', isActive ? 'bg-white text-primary-800 shadow-xs' : 'text-neutral-600 hover:text-neutral-800', className), disabled: disabled, id: tabId, onClick: () => onChange(value), role: 'tab', tabIndex: isActive ? 0 : -1, type: 'button', children: children }));
    }
    return ((0, jsx_runtime_1.jsx)("button", { "aria-controls": panelId, "aria-selected": isActive, className: (0, utils_1.cn)('-mb-px cursor-pointer border-0 border-b-2 border-transparent bg-transparent px-0 py-3.5 text-sm font-semibold transition-colors', 'focus-visible:outline-none focus-visible:shadow-[var(--aiq-ring-focus)]', 'disabled:cursor-not-allowed disabled:opacity-50', isActive ? 'border-primary text-primary' : 'text-neutral-500 hover:text-neutral-700', className), disabled: disabled, id: tabId, onClick: () => onChange(value), role: 'tab', tabIndex: isActive ? 0 : -1, type: 'button', children: children }));
}
function TabPanel({ value, forceMount = false, className, children }) {
    const { value: selected, idBase } = useTabs();
    const isActive = selected === value;
    if (!isActive && !forceMount) {
        return null;
    }
    return ((0, jsx_runtime_1.jsx)("div", { "aria-labelledby": `${idBase}-tab-${value}`, className: className, hidden: !isActive, id: `${idBase}-panel-${value}`, role: 'tabpanel', children: children }));
}
